/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind.js';
import { arrayOf, extend, params, shape, string, useDataModel } from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import { Button, Card, CardContent, Typography } from '@one-thd/sui-atomic-components';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { HomeServicesSubList } from './HomeServicesSubList';
import styles from '../service-category.module.scss';

const { string: stringType, number: numberType, bool: boolType } = proptypes;

const cx = classNames.bind(styles);
const containerCardCSS = 'sui-flex sui-flex-col sui-bg-primary sui-p-4 sui-gap-4';

export const HomeServices = ({
  title,
  storeZip,
  categoryLimit = 6,
  subCategoryLimit = 7,
  displayAllCategories = false,
  className = ''
}) => {
  const [viewAllCategories, setViewAllCategories] = useState(true);
  const { data, loading, error } = useDataModel('storeServices', { variables: { zipCode: storeZip } });
  const services = data?.storeServices?.storeServices;

  useEffect(() => {
    setViewAllCategories((displayAllCategories || false));
  }, []);

  const handleAllCategories = () => {
    setViewAllCategories(true);
  };

  if (!data || loading || error || !services || services?.length === 0) return null;

  return (
    <div data-component="HomeServiceCategory" className={`${className} ${containerCardCSS}`}>
      <Typography variant="h2" weight="display">{title}</Typography>
      <div className="sui-grid sui-grid-cols-1 md:sui-grid-cols-2 lg:sui-grid-cols-3 sui-gap-8">
        { services?.map((service, index) => {
          return (viewAllCategories || index < categoryLimit) && (
            <Card
              grow
              CardContentProps={{ className: 'sui-flex sui-p-4 sui-gap-4 sui-flex-col sui-grow' }}
              key={`home-service-card-${index}`}
            >
              <CardContent disablePadding disableGutters className="sui-flex sui-flex-col">
                <LoadingPlaceholder
                  type="rect"
                  ready={!loading}
                  showLoadingAnimation
                  style={{
                    height: '170px',
                    width: '300px',
                  }}
                  color="#eeeeee"
                >
                  <img
                    height="auto"
                    width="100%"
                    loading="lazy"
                    src={service?.imageUrl}
                    title={service?.name}
                    alt={service?.name}
                    className={cx('services-card__image')}
                  />
                </LoadingPlaceholder>
                <LoadingPlaceholder
                  type="text"
                  ready={!loading}
                  color="#E0E0E0"
                  rows={9}
                  showLoadingAnimation
                >
                  <h2 className="sui-font-light sui-text-5xl">{service?.name}</h2>
                  <ul>
                    <HomeServicesSubList service={service} subCategoryLimit={subCategoryLimit} />
                  </ul>
                </LoadingPlaceholder>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {!viewAllCategories && services.length > categoryLimit && (
        <div className="sui-flex sui-justify-center sui-mt-5">
          <div className="md:sui-w-full lg:sui-w-96">
            <Button
              onClick={handleAllCategories}
              data-testid="services-card__view-all"
              variant="secondary"
              fullWidth
            >
              View All Categories
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

HomeServices.displayName = 'HomeServices';

HomeServices.propTypes = {
  className: stringType,
  storeZip: stringType.isRequired,
  title: stringType.isRequired,
  categoryLimit: numberType,
  subCategoryLimit: numberType,
  displayAllCategories: boolType
};

HomeServices.dataModel = extend({
  storeServices: params({ zipCode: string().isRequired() }).shape({
    storeServices: arrayOf(shape({
      name: string(),
      namespace: string(),
      imageUrl: string(),
      storeServiceSubCategories: arrayOf(shape({
        name: string(),
        rank: string(),
        leadPageUrl: string()
      })),
    })),
  }),
});
